import React, { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "ol/ol.css";

import View from "ol/View";
import { fromLonLat } from "ol/proj";

import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import LayerGroup from "ol/layer/Group";

import GeoJSON from "ol/format/GeoJSON";

import Feature from "ol/Feature";
import Point from "ol/geom/Point";

import {
  Style,
  Circle as CircleStyle,
  Fill,
  Stroke,
  Text,
} from "ol/style";

import moment from "moment-timezone";

import NysmMap from "../modules/nysm/NysmMap";
import TimeManager from "../modules/nysm/TimeManager/TimeManager";

import { base_layers, updateIncidentLayer } from "./base_layers";
import { WimsLayers, getNFDRSStyle } from "./wims_layers";
import { getColorForValue } from "./colorMapping";
import { useFDRAColors } from "./use_fdra_colors";
import {
    watch as watchNwsAlerts,
    unwatch as unwatchNwsAlerts,
    selectAlerts
} from "../redux-modules/current-nws-alerts";
const styleCache = {};

const MapContainer = ({
  visibleLayers,
  stationData,
  selectedVariable,
  setStationName,
  isNFDRS,
  display,
  forecastData,
  colorInfo,
}) => {
  const mapDivRef = useRef(null);

  const nysmMapRef = useRef(null);
  const [olMap, setOlMap] = useState(null);
  const stationLayerRef = useRef(null);
  const forecastLayerRef = useRef(null);

  const radarLayerRef = useRef(null);
  const lightningLayerRef = useRef(null);
  const alertsLayerRef = useRef(null);
  const alerts_area = "NY";
  const dispatch = useDispatch();
  const alerts = useSelector(selectAlerts);
  const watchAlerts = useCallback((area, watcher) => dispatch(watchNwsAlerts(area, watcher)), [dispatch]);
  const unwatchAlerts = useCallback((area, watcher) => dispatch(unwatchNwsAlerts(area, watcher)), [dispatch]);
  const alertsWatcherId = useRef("map-alerts");

  const groups = useRef({
    base: new LayerGroup(),
    radar: new LayerGroup(),
    alerts: new LayerGroup(),
    middle: new LayerGroup(),
    top: new LayerGroup(),
  });

  const timeManager = useRef(
    new TimeManager(
      moment.utc(),
      moment.utc(),
      moment.duration(1, "minute"),
      {
        time: 50,
        dwell: 3000,
      }
    )
  );

  // ======================================================
  // INIT MAP
  // ======================================================

  useEffect(() => {
    const nysmMap = new NysmMap(
      {
        layers: [
          groups.current.base,
          groups.current.radar,
          groups.current.alerts,
          groups.current.middle,
          groups.current.top,
        ],
      },
      {
        target: mapDivRef.current,
        timeManager: timeManager.current,
      }
    );

    nysmMap.map.setView(
      new View({
        center: fromLonLat([-75.5, 42.9]),
        zoom: 7,
      })
    );

    // --------------------------------------------------
    // EXISTING OL LAYERS
    // --------------------------------------------------

    base_layers.forEach((layer) => {
      const collection = groups.current.base.getLayers();

      if (!collection.getArray().includes(layer)) {
        collection.push(layer);
      }
    });

    // --------------------------------------------------
    // FIT TO CHGE
    // --------------------------------------------------

    const chgeLayer = base_layers[0];

    if (chgeLayer?.getSource) {
      chgeLayer.getSource().once("change", () => {
        if (chgeLayer.getSource().getState() === "ready") {
          const extent = chgeLayer.getSource().getExtent();

          nysmMap.map.getView().fit(extent, {
            padding: [50, 50, 50, 50],
          });
        }
      });
    }

    nysmMapRef.current = nysmMap;
    setOlMap(nysmMap.map);
    return () => {
      nysmMap.destroy();
    };
  }, []);

  // ======================================================
  // FDRA COLORS
  // ======================================================

  useFDRAColors(
    {
      current: olMap,
    },
    colorInfo
  );

  // ======================================================
  // BASE LAYER VISIBILITY
  // ======================================================

  useEffect(() => {
    base_layers[0]?.setVisible(visibleLayers?.chge);
    base_layers[1]?.setVisible(visibleLayers?.nys);
    base_layers[2]?.setVisible(visibleLayers?.counties);
    base_layers[4]?.setVisible(visibleLayers?.fdras);
    base_layers[5]?.setVisible(visibleLayers?.incidents);
  }, [visibleLayers]);

  // ======================================================
  // RADAR
  // ======================================================

  useEffect(() => {
    if (!nysmMapRef.current) return;

    if (visibleLayers?.radar && !radarLayerRef.current) {
      const radarLayer = {
        type: "noaa-mapservices-bref",
        title: "Radar",
        group: groups.current.radar,
      };

      nysmMapRef.current.addLayer(radarLayer);

      radarLayerRef.current = radarLayer;
    }

    if (!visibleLayers?.radar && radarLayerRef.current) {
      nysmMapRef.current.removeLayer(radarLayerRef.current);

      radarLayerRef.current = null;
    }
  }, [visibleLayers?.radar]);

  // ======================================================
  // LIGHTNING
  // ======================================================

  useEffect(() => {
    if (!nysmMapRef.current) return;

    if (
      visibleLayers?.lightning &&
      !lightningLayerRef.current
    ) {
      const lightningLayer = {
        type: "nysm-lightning",
        title: "Lightning",
        group: groups.current.top,
      };

      nysmMapRef.current.addLayer(lightningLayer);

      lightningLayerRef.current = lightningLayer;
    }

    if (
      !visibleLayers?.lightning &&
      lightningLayerRef.current
    ) {
      nysmMapRef.current.removeLayer(
        lightningLayerRef.current
      );

      lightningLayerRef.current = null;
    }
  }, [visibleLayers?.lightning]);

  // ======================================================
  // ALERTS
  // ======================================================
    useEffect(() => {
        if (!nysmMapRef.current) return;

        watchAlerts(alerts_area, alertsWatcherId.current);

        return () => {
            unwatchAlerts(alerts_area, alertsWatcherId.current);
        };
    }, [watchAlerts, unwatchAlerts]);

    // useEffect(() => {
    //     if (!nysmMapRef.current) return;

    //     if (visibleLayers?.alerts && !alertsLayerRef.current) {
    //         const alertsLayer = {
    //         type: "nws-alerts",
    //         title: "Alerts",
    //         group: groups.current.alerts,
    //         alerts: [],

    //         // IMPORTANT: hook for redux feed
    //         onNewAlerts: null,
    //         };

    //         nysmMapRef.current.addLayer(alertsLayer);
    //         alertsLayerRef.current = alertsLayer;
    //     }

    //     if (!visibleLayers?.alerts && alertsLayerRef.current) {
    //         nysmMapRef.current.removeLayer(alertsLayerRef.current);
    //         alertsLayerRef.current = null;
    //     }
    // }, [visibleLayers?.alerts]);

    useEffect(() => {
        if (!alertsLayerRef.current) return;

        const area = alerts?.[alerts_area];

        if (!area?.alerts || !area?.zonesLoaded) return;

        alertsLayerRef.current.onNewAlerts?.(area.alerts);
    }, [alerts]);


    useEffect(() => {
        if (!nysmMapRef.current || alertsLayerRef.current) return;

        const alertsLayer = {
            type: "nws-alerts",
            title: "Alerts",
            group: groups.current.alerts,
            alerts: [],
            onNewAlerts: null,
        };
        console.log(alertsLayer)

        nysmMapRef.current.addLayer(alertsLayer);

        alertsLayerRef.current = alertsLayer;
    }, []);

    useEffect(() => {
        groups.current.alerts.setVisible(
            !!visibleLayers?.alerts
        );
    }, [visibleLayers?.alerts]);
  // ======================================================
  // STATIONS
  // ======================================================

  useEffect(() => {
    if (!nysmMapRef.current) return;

    // remove previous
    if (stationLayerRef.current) {
      groups.current.top
        .getLayers()
        .remove(stationLayerRef.current);

      stationLayerRef.current = null;
    }

    if (display !== "obs") return;

    const layer = createStationLayer();

    updateStationLayer({
      layer,
      stationData,
      selectedVariable,
      isNFDRS,
    });

    groups.current.top.getLayers().push(layer);

    stationLayerRef.current = layer;
  }, [
    stationData,
    selectedVariable,
    isNFDRS,
    display,
  ]);

  // ======================================================
  // FORECAST
  // ======================================================

  useEffect(() => {
    if (!nysmMapRef.current) return;

    if (forecastLayerRef.current) {
      groups.current.middle
        .getLayers()
        .remove(forecastLayerRef.current);

      forecastLayerRef.current = null;
    }

    if (display !== "forecast" || !forecastData)
      return;

    const forecastLayer = createForecastLayer(
      forecastData
    );

    groups.current.middle
      .getLayers()
      .push(forecastLayer);

    forecastLayerRef.current = forecastLayer;
  }, [display, forecastData]);


  // ======================================================
  // HOVER + CLICK
  // ======================================================

  useEffect(() => {
    if (!nysmMapRef.current) return;

    const map = nysmMapRef.current.map;

    const tooltip = document.getElementById(
      "map-tooltip"
    );

    const lastHoverRef = {
      current: null,
    };

    const stationHighlight = new Style({
      image: new CircleStyle({
        radius: 20,
        fill: new Fill({
          color: "rgba(0,153,255,0.9)",
        }),
        stroke: new Stroke({
          color: "#001aff",
          width: 2,
        }),
      }),
    });

    const incidentHighlight = new Style({
      image: new CircleStyle({
        radius: 18,
        fill: new Fill({
          color: "rgba(255,80,0,0.9)",
        }),
        stroke: new Stroke({
          color: "#ffcc00",
          width: 2,
        }),
      }),
    });

    const onPointerMove = (evt) => {
      if (evt.dragging) return;

      const feature = map.forEachFeatureAtPixel(
        evt.pixel,
        (f) => f
      );

      // --------------------------------------------
      // RESET
      // --------------------------------------------

      if (!feature) {
        if (lastHoverRef.current) {
          lastHoverRef.current.setStyle(
            lastHoverRef.current._originalStyle ||
              null
          );

          lastHoverRef.current = null;
        }

        if (tooltip) {
          tooltip.style.display = "none";
        }

        return;
      }

      const type = feature.get("type");

      if (!type) return;

      // --------------------------------------------
      // RESTORE PREVIOUS
      // --------------------------------------------

      if (
        lastHoverRef.current &&
        lastHoverRef.current !== feature
      ) {
        lastHoverRef.current.setStyle(
          lastHoverRef.current._originalStyle ||
            null
        );
      }

      // --------------------------------------------
      // STORE ORIGINAL
      // --------------------------------------------

      if (
        type !== "incident" &&
        type !== "nfdr_station" &&
        !feature._originalStyle
      ) {
        feature._originalStyle = feature.getStyle();
      }

      // --------------------------------------------
      // HIGHLIGHT
      // --------------------------------------------

      if (
        type === "station" ||
        type === "nfdr_station"
      ) {
        feature.setStyle(stationHighlight);
      }

      if (type === "incident") {
        feature.setStyle(incidentHighlight);
      }

      lastHoverRef.current = feature;

      // --------------------------------------------
      // TOOLTIP
      // --------------------------------------------

      if (tooltip) {
        let html = "";
        if (type === "station") {
          const name = feature.get("station_name");
          const val = feature.get("value");

          html = `
            <strong>${name}</strong><br/>
            Value: ${val ?? "N/A"}
          `;
        }

        if (type === "nws_alert") {
        html = `
            <strong>${feature.get("event")}</strong><br/>

        `;
        }

        if (type === "nfdr_station") {
          const name = feature.get("station_name");
          const val = feature.get(selectedVariable);

          html = `
            <strong>${name}</strong><br/>
            Value: ${val ?? "N/A"}
          `;
        }

        if (type === "incident") {
          const name = feature.get("IncidentName");
          const size = feature.get("IncidentSize");
          const contained =
            feature.get("PercentContained");

          html = `
            <strong>${name}</strong><br/>
            Size: ${
              size?.toLocaleString() ?? "N/A"
            } acres<br/>
            Contained: ${contained ?? "N/A"}%
          `;
        }

        if (!html) {
          tooltip.style.display = "none";
          return;
        }

        tooltip.innerHTML = html;

        tooltip.style.display = "block";

        const [x, y] = evt.pixel;

        tooltip.style.left = x + 12 + "px";
        tooltip.style.top = y + 12 + "px";
      }
    };

    const onClick = (evt) => {
      const feature = map.forEachFeatureAtPixel(
        evt.pixel,
        (f, layer) => {
          if (layer === stationLayerRef.current) {
            return f;
          }

          return null;
        }
      );

      if (feature) {
        const stationName =
          feature.get("station_name");

        setStationName(stationName);
      } else {
        setStationName(null);
      }
    };

    map.on("pointermove", onPointerMove);
    map.on("click", onClick);

    return () => {
      map.un("pointermove", onPointerMove);
      map.un("click", onClick);
    };
  }, [selectedVariable, setStationName]);

  // ======================================================
  // INCIDENT REFRESH
  // ======================================================

  useEffect(() => {
    const incidentLayer = base_layers.find(
      (l) => l.get("layerType") === "incidents"
    );

    if (!incidentLayer) return;

    updateIncidentLayer(incidentLayer);

    const interval = setInterval(() => {
      updateIncidentLayer(incidentLayer);
    }, 8 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      <div
        id="map-tooltip"
        style={{
          position: "absolute",
          pointerEvents: "none",
          display: "none",
          zIndex: 9999,
          background: "rgba(0,0,0,0.85)",
          color: "#fff",
          padding: "6px 10px",
          borderRadius: "4px",
        //   fontSize: "12px",
          whiteSpace: "nowrap",
        }}
      />

      <div
        ref={mapDivRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

// ======================================================
// STATIONS
// ======================================================

const createStationLayer = () => {
  const layer = new VectorLayer({
    source: new VectorSource(),
    updateWhileAnimating: true,
    updateWhileInteracting: true,
  });

  layer.set("layerType", "stations");

  return layer;
};

const updateStationLayer = ({
  layer,
  stationData,
  selectedVariable,
  isNFDRS,
}) => {
  const src = layer.getSource();

  src.clear();

  if (!stationData || stationData.length === 0)
    return;

  // --------------------------------------------------
  // NFDRS
  // --------------------------------------------------

  if (isNFDRS) {
    const features = WimsLayers(stationData);

    src.addFeatures(features);

    layer.setStyle((f) =>
      getNFDRSStyle(selectedVariable, f)
    );

    return;
  }

  // --------------------------------------------------
  // NORMAL STATIONS
  // --------------------------------------------------

  layer.setStyle(null);

  const features = stationData
    .map((station) => {
      const isSawti =
        selectedVariable === "sawti";

      const lon = isSawti
        ? station.lon
        : station.LON;

      const lat = isSawti
        ? station.lat
        : station.LAT;

      if (
        lon === undefined ||
        lat === undefined
      ) {
        return null;
      }

      const name = isSawti
        ? station.station
        : station.WeatherRegion;

      const value = parseFloat(
        station[selectedVariable]
      );

      const f = new Feature({
        geometry: new Point(
          fromLonLat([lon, lat])
        ),

        station_name: name,
        value,
        type: "station",
      });

      f.setStyle(
        getStationStyle(selectedVariable, value)
      );

      return f;
    })
    .filter(Boolean);

  src.addFeatures(features);
};

const getStationStyle = (variable, value) => {
  const key = `${variable}-${value}`;

  if (!styleCache[key]) {
    styleCache[key] = new Style({
      image: new CircleStyle({
        radius: 14,

        fill: new Fill({
          color: getColorForValue(variable, value),
        }),

        stroke: new Stroke({
          color: "#444",
          width: 1,
        }),
      }),

      text: new Text({
        text: isNaN(value)
          ? ""
          : String(roundTo(value, 0)),

        font: "bold 12px sans-serif",

        fill: new Fill({
          color: "#fff",
        }),

        stroke: new Stroke({
          color: "#000",
          width: 1,
        }),
      }),
    });
  }

  return styleCache[key];
};

// ======================================================
// FORECAST
// ======================================================

const createForecastLayer = (geojson) => {
  const features = new GeoJSON().readFeatures(
    geojson,
    {
      featureProjection: "EPSG:3857",
    }
  );

  return new VectorLayer({
    source: new VectorSource({
      features,
    }),

    style: contourStyle,

    updateWhileAnimating: true,
    updateWhileInteracting: true,
  });
};

// ======================================================
// CONTOUR STYLE
// ======================================================

function contourStyle(feature) {
  const title = feature.get("title");

  if (!title) return null;

  const [min, max] = title
    .split("-")
    .map((v) => parseFloat(v.trim()));

  const value = (min + max) / 2;

  let color;

  if (value < 5) color = "#1e6f9e";
  else if (value < 10) color = "#88bcda";
  else if (value < 20) color = "#ffd17b";
  else if (value < 30) color = "#ff9249";
  else if (value < 40) color = "#f34e02";
  else if (value < 50) color = "#ff4040";
  else color = "#c70053";

  return new Style({
    fill: new Fill({
      color: color + "99",
    }),
  });
}

function roundTo(num, decimals) {
  const factor = 10 ** decimals;

  return Math.round(num * factor) / factor;
}

export default MapContainer;